import { MarketEntry } from '../types'
import { SparkRewardsByReserve } from './useSparkRewardsByReserve'
import { sortByUsdValue } from '@/domain/common/sorters'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { MarketInfo, Reserve } from '@/features/sparklend/common/data/market-info/marketInfo'
import { InternalLink } from '@/routing/types'
import { applyTransformers, Transformer, TransformerResult } from '@/utils/applyTransformers'

type MarketEntryTransformer = Transformer<
  [number, Reserve, Reserve[], SparkRewardsByReserve],
  TransformerResult<MarketEntry>
>

function getTransformers(): MarketEntryTransformer[] {
  return [skipInactiveReserves, makeMarketEntry]
}

export function transformReserves(marketInfo: MarketInfo, sparkRewardsByReserve: SparkRewardsByReserve): MarketEntry[] {
  const transformers = getTransformers()
  const { nativeAssetInfo } = marketInfo
  const reserves = marketInfo.reserves.map((r) => {
    if (r.token.symbol === nativeAssetInfo.wrappedNativeAssetSymbol) {
      return {
        ...r,
        token: r.token.clone({
          symbol: nativeAssetInfo.nativeAssetSymbol,
          name: nativeAssetInfo.nativeAssetName,
          logo: nativeAssetInfo.nativeAssetLogo,
        }),
      }
    }
    return r
  })
  const entries = reserves
    .map((r) => {
      return applyTransformers(marketInfo.chainId, r, reserves, sparkRewardsByReserve)(transformers)
    })
    .filter((r): r is MarketEntry => r !== null)
    .sort((a, b) => -sortByUsdValue(a, b, 'totalSupplied')) // this is needed for mobile view where we don't have sorting functions

  if (import.meta.env.VITE_FEATURE_BORROW_USDC_FROM_USDS === '1') {
    return extendEntriesOfUsdcViaUsdsMarket({ entries, chainId: marketInfo.chainId })
  }

  return entries
}

function skipInactiveReserves(
  _: number,
  reserve: Reserve,
  _reserves: Reserve[],
  _sparkRewardsByReserve: SparkRewardsByReserve,
): undefined | null {
  if (reserve.status === 'not-active') return null

  return undefined
}

function extendEntriesOfUsdcViaUsdsMarket({
  entries,
  chainId,
}: {
  entries: MarketEntry[]
  chainId: number
}): MarketEntry[] {
  const usdsEntryIndex = entries.findIndex((entry) => entry.token.symbol === TokenSymbol('USDS'))
  const usdsEntry = entries[usdsEntryIndex]
  if (!usdsEntry) return entries

  const usdcEntry = entries.find((entry) => entry.token.symbol === TokenSymbol('USDC'))
  if (!usdcEntry) return entries

  const proxy: MarketEntry = {
    ...usdsEntry,
    link: InternalLink({
      to: '/markets/$chainId/$asset',
      params: { chainId, asset: usdsEntry.token.address },
      search: { 'usdc-proxy-market': true },
    }),
    proxyMarket: {
      name: 'USDC via USDS',
      tokenSymbol: TokenSymbol('USDC'),
      tokenLogo: usdcEntry.token.logo,
      underlyingReserveLogo: usdsEntry.token.logo,
      description: 'Borrow USDS and swap 1:1 for USDC.',
    },
  }

  const result = [...entries]
  result.splice(usdsEntryIndex + 1, 0, proxy)
  return result
}

export function makeMarketEntry(
  chainId: number,
  reserve: Reserve,
  _reserves: Reserve[],
  sparkRewardsByReserve: SparkRewardsByReserve,
): MarketEntry {
  const sparkRewards = sparkRewardsByReserve[reserve.token.address]

  return {
    token: reserve.token,
    reserveStatus: reserve.status,
    totalSupplied: reserve.totalLiquidity,
    depositApyDetails: {
      baseApy: reserve.supplyAPY,
      sparkRewards: sparkRewards?.supply,
    },
    totalBorrowed: reserve.totalDebt,
    borrowApyDetails: {
      baseApy: reserve.variableBorrowApy,
      sparkRewards: sparkRewards?.borrow,
    },
    marketStatus: {
      supplyAvailabilityStatus: reserve.supplyAvailabilityStatus,
      collateralEligibilityStatus: reserve.collateralEligibilityStatus,
      borrowEligibilityStatus: reserve.borrowEligibilityStatus,
    },
    link: InternalLink({
      to: '/markets/$chainId/$asset',
      params: { chainId, asset: reserve.token.address },
    }),
    sllManaged: reserve.sllManaged,
  }
}
